import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Bot, Loader2, Menu, Pin, PinOff, Plus, Table2 } from "lucide-react";

import { AgentInfoTag } from "@/components/agent-badge";
import { AgentConversationsVirtualizedTable } from "@/components/agent-conversations-table";
import { NotificationInbox } from "@/components/notification-inbox";
import { SettingsMenu } from "@/components/settings-menu";
import { AgentViewPanel } from "@/components/views/agent-view-panel";
import { ViewTabs } from "@/components/views/view-tabs";
import {
  agentViewTab,
  launchableBrowserMounts,
  viewPagePath,
  viewRefKey,
  type AgentViewSummary,
  type ViewTab,
} from "@/api/views";
import type { BrowserMount } from "@/components/views/file-browser-scope";
import { parseFilesMount } from "@/hooks/use-view-deep-link";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNotificationContext } from "@/components/notification-provider";
import type { Agent } from "@/hooks/use-agents";
import { useAgentSessions } from "@/hooks/use-agent-sessions";
import { useDraftSessionIds } from "@/hooks/use-draft-session-ids";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";
import type { PushSubscriptionAPI } from "@/hooks/use-push-subscription";
import type { UseUserPreferencesReturn } from "@/hooks/use-user-preferences";
import { isProjectTaskSession } from "@/lib/session";
import type { AccessTokenGetter } from "@/lib/utils";

const SESSIONS_VIEW_TAB_KEY = "sessions";

export interface AgentConversationsPageProps {
  selectedAgent?: Agent;
  agents: Agent[];
  agentsLoading: boolean;
  /** The RBAC-filtered catalog has completed its first successful load. */
  agentCatalogLoaded: boolean;
  agentsError: string | null;
  isAdminUser: boolean;
  canManageTenantAgents?: boolean;
  user: { firstName?: string | null; email?: string | null };
  pushApi: PushSubscriptionAPI;
  preferencesApi: UseUserPreferencesReturn;
  pins: string[];
  getToken: AccessTokenGetter;
  onPin: (agentId: string) => void | Promise<unknown>;
  onUnpin: (agentId: string) => void | Promise<unknown>;
  onCreateConversation: (agentId: string) => void | Promise<void>;
  onOpenSession: (sessionId: string) => void;
  /** Shared manager selection for this route's otherwise-suppressed aside. */
  activeViewId: string | null;
  /** True when the shell aside manager owns any panel target, not just a view. */
  hasActiveAsideTarget?: boolean;
  onOpenAgentView: (viewId: string) => void;
  onCloseAgentView: () => void;
  /** The shell-owned resize controller, selected as the agent panel kind. */
  resize: AsidePanelResize;
  isMenuOpen?: boolean;
  onMenuToggle?: () => void;
}

function hasViewId(view: unknown): view is AgentViewSummary {
  return (
    Boolean(view)
    && typeof view === "object"
    && typeof (view as Record<string, unknown>).id === "string"
  );
}

/**
 * Deep-linkable exact-agent conversation hub. The parent validates the
 * RBAC-filtered agent object, while this component passes null to its session
 * hook until that validation succeeds so an arbitrary route cannot leak data.
 */
export function AgentConversationsPage({
  selectedAgent,
  agents,
  agentsLoading,
  agentCatalogLoaded,
  agentsError,
  isAdminUser,
  canManageTenantAgents,
  user,
  pushApi,
  preferencesApi,
  pins,
  getToken,
  onPin,
  onUnpin,
  onCreateConversation,
  onOpenSession,
  activeViewId,
  hasActiveAsideTarget = false,
  onOpenAgentView,
  onCloseAgentView,
  resize,
  isMenuOpen = false,
  onMenuToggle,
}: AgentConversationsPageProps) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const isMobile = useIsMobile();
  const [creating, setCreating] = useState(false);
  const [pinMutating, setPinMutating] = useState(false);
  const autoOpenLandingRef = useRef<{ agentId: string | null; opened: boolean }>({
    agentId: null,
    opened: false,
  });
  const notificationContext = useNotificationContext();
  const draftSessionIds = useDraftSessionIds();
  const selectableAgents = useMemo(
    () => agents.filter((agent) => agent.type === "agent"),
    [agents],
  );
  const validAgent = selectedAgent?.type === "agent" ? selectedAgent : undefined;
  const {
    sessions,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMore,
  } = useAgentSessions(validAgent?.id ?? null, getToken);

  const viewTabs = useMemo(() => {
    if (!validAgent || !Array.isArray(validAgent.views)) return [];
    return validAgent.views.flatMap((view) => (
      hasViewId(view) ? [agentViewTab(validAgent.id, view)] : []
    ));
  }, [validAgent]);
  const selectedView = viewTabs.find((view) => viewRefKey(view.ref) === activeViewId);
  const browserMounts = useMemo<BrowserMount[]>(() => launchableBrowserMounts(validAgent?.views)
    .map((view) => ({ id: view.id, title: view.title, mount_id: view.mount_id! })), [validAgent?.views]);
  const ownerPath = validAgent ? `/agent/${encodeURIComponent(validAgent.id)}/conversations` : "";
  const mountParams = new URLSearchParams(window.location.search);
  const ownsMountHint = mountParams.get("filesMountFor") === ownerPath && mountParams.has("filesMount");
  const hintedMount = ownsMountHint ? parseFilesMount(mountParams.get("filesMount") ?? "") : null;
  const browserMountHintInvalid = Boolean(ownsMountHint && selectedView?.type === "file_browser"
    && (!hintedMount || browserMounts.find((mount) => mount.id === selectedView.id)?.mount_id !== hintedMount));
  const changeBrowserMount = (mount: BrowserMount) => {
    const url = new URL(window.location.href);
    url.searchParams.set("panel", `agent:${mount.id}`);
    url.searchParams.set("filesMount", mount.mount_id);
    url.searchParams.set("filesMountFor", ownerPath);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const defaultView = useMemo(() => {
    const defaults = viewTabs.filter((view) => view.isDefault === true);
    return defaults.length === 1 ? defaults[0] : null;
  }, [viewTabs]);

  // `activeViewId` is intentionally null for file, thread, and sub-agent
  // panels. The shell supplies the broader target state from its synchronous
  // URL parser, so a landing default cannot clobber any deep-linked aside.
  useEffect(() => {
    const validAgentId = validAgent?.id ?? null;
    if (!validAgentId) {
      autoOpenLandingRef.current = { agentId: null, opened: false };
      return;
    }

    if (autoOpenLandingRef.current.agentId !== validAgentId) {
      autoOpenLandingRef.current = { agentId: validAgentId, opened: false };
    }
    if (
      agentsLoading
      || !defaultView
      || activeViewId !== null
      || hasActiveAsideTarget
      || autoOpenLandingRef.current.opened
    ) {
      return;
    }

    // Mark before navigating: the URL/state update is asynchronous, while a
    // user closing this view must remain on Sessions for this same landing.
    autoOpenLandingRef.current.opened = true;
    if (defaultView.type === "visual_editor") {
      if (isMobile) navigate(viewPagePath(defaultView.ref), { replace: true });
    } else {
      onOpenAgentView(viewRefKey(defaultView.ref));
    }
  }, [
    activeViewId,
    agentsLoading,
    defaultView,
    hasActiveAsideTarget,
    isMobile,
    navigate,
    onOpenAgentView,
    validAgent?.id,
  ]);

  // The shell's aside manager is the only panel URL reader/writer. Once this
  // route has loaded its RBAC-filtered view list, retire a stale deep link
  // rather than leaving an empty right edge. `agentsLoading` starts false on
  // a fresh mount, before the first catalog request has even begun.
  useEffect(() => {
    if (!activeViewId || selectedView || agentsLoading || !agentCatalogLoaded) return;
    onCloseAgentView();
  }, [activeViewId, agentCatalogLoaded, agentsLoading, onCloseAgentView, selectedView]);

  useEffect(() => {
    if (selectedView?.type === "visual_editor") {
      if (isMobile) navigate(viewPagePath(selectedView.ref), { replace: true });
      else onCloseAgentView();
    }
  }, [isMobile, navigate, onCloseAgentView, selectedView]);

  const visiblePinnedCount = useMemo(() => {
    const accessibleIds = new Set(selectableAgents.map((agent) => agent.id));
    return pins.filter((id) => accessibleIds.has(id)).length;
  }, [pins, selectableAgents]);
  const pinned = validAgent ? pins.includes(validAgent.id) : false;
  const cannotUnpin = pinned && visiblePinnedCount <= 1;

  const handlePin = async () => {
    if (!validAgent || pinMutating || cannotUnpin) return;
    setPinMutating(true);
    try {
      if (pinned) await onUnpin(validAgent.id);
      else await onPin(validAgent.id);
    } finally {
      setPinMutating(false);
    }
  };

  const handleCreate = async () => {
    if (!validAgent || creating) return;
    setCreating(true);
    try {
      await onCreateConversation(validAgent.id);
    } finally {
      setCreating(false);
    }
  };

  const handleViewSelect = (view: ViewTab) => {
    if (view.type === "visual_editor") {
      if (isMobile) navigate(viewPagePath(view.ref));
      else window.open(viewPagePath(view.ref), "_blank", "noopener");
      return;
    }
    onOpenAgentView(viewRefKey(view.ref));
  };

  const handleCloseView = () => {
    const returnToFilesIcon = ownsMountHint && selectedView?.type === "file_browser" && validAgent;
    onCloseAgentView();
    if (returnToFilesIcon) {
      window.requestAnimationFrame(() => {
        const target = [...document.querySelectorAll<HTMLButtonElement>("[data-testid^='sidebar-agent-files-']")]
          .find((button) => button.dataset.testid === `sidebar-agent-files-${returnToFilesIcon.id}`);
        target?.focus();
      });
    }
  };

  if (agentsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t("common:status.loading")}
      </div>
    );
  }

  if (!validAgent) {
    return (
      <main className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <Bot className="size-7 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-destructive">{agentsError ?? t("agent.agentUnavailable")}</p>
      </main>
    );
  }

  const footer = () => {
    if (loadingMore) {
      return <div className="flex justify-center py-3"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>;
    }
    if (!hasMore) return null;
    return (
      <div className="flex justify-center py-3">
        <Button type="button" variant="ghost" size="sm" onClick={() => { void loadMore(); }}>
          {t("agent.loadMoreConversations")}
        </Button>
      </div>
    );
  };

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex min-h-[53px] min-w-0 items-center gap-2 border-b px-3 py-2.5">
        {isMobile && onMenuToggle && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onMenuToggle}
            aria-label={t(isMenuOpen ? "header.closeMenu" : "header.openMenu")}
          >
            <Menu className="size-4" />
          </Button>
        )}
        <Bot className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="min-w-0 shrink truncate text-sm font-semibold">{validAgent.name}</span>
        <AgentInfoTag
          agentId={validAgent.id}
          agent={validAgent}
          getToken={getToken}
          className="ml-auto min-w-0"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={pinMutating || cannotUnpin}
                  onClick={() => { void handlePin(); }}
                  aria-label={pinned
                    ? t("agent.unpinAgent", { name: validAgent.name })
                    : t("agent.pinAgent", { name: validAgent.name })}
                >
                  {pinMutating
                    ? <Loader2 className="size-3.5 animate-spin" />
                    : pinned
                      ? <PinOff className="size-3.5 stroke-[1.5] fill-none text-muted-foreground/60" />
                      : <Pin className="size-3.5 stroke-[1.5] fill-none text-muted-foreground/60" />}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {cannotUnpin
                ? t("agent.cannotUnpinOnlyAgent")
                : pinned ? t("agent.unpinAgent", { name: validAgent.name }) : t("agent.pinAgent", { name: validAgent.name })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={creating}
                onClick={() => { void handleCreate(); }}
                aria-label={t("sidebar.createConversationWithAgent", { name: validAgent.name })}
              >
                {creating ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("sidebar.createConversationWithAgent", { name: validAgent.name })}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {isMobile && <>
          <NotificationInbox />
          <SettingsMenu
            className="shrink-0"
            user={user}
            isAdminUser={isAdminUser}
            canManageTenantAgents={canManageTenantAgents}
            pushApi={pushApi}
            preferencesApi={preferencesApi}
            agents={agents}
            agentsLoading={agentsLoading}
            agentsError={agentsError}
          />
        </>}
      </header>

      {viewTabs.length > 0 && (
        <ViewTabs
          views={viewTabs}
          activeViewKey={selectedView ? viewRefKey(selectedView.ref) : SESSIONS_VIEW_TAB_KEY}
          leadingTab={{
            key: SESSIONS_VIEW_TAB_KEY,
            title: t("agent.sessions"),
            icon: Table2,
            onSelect: handleCloseView,
          }}
          onSelect={handleViewSelect}
        />
      )}

      {selectedView && selectedView.type !== "visual_editor" ? (
        <AgentViewPanel
          viewRef={selectedView.ref}
          title={selectedView.title}
          viewType={selectedView.type}
          viewConfig={selectedView.config}
          browserMounts={browserMounts}
          onBrowserMountChange={changeBrowserMount}
          browserMountHintInvalid={browserMountHintInvalid}
          getToken={getToken}
          onClose={handleCloseView}
          resize={resize}
          fullscreen
        />
      ) : loading && sessions.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : error && sessions.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-destructive">
          {t("agent.conversationsLoadFailed")}
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t("agent.noConversations")}
        </div>
      ) : (
        <AgentConversationsVirtualizedTable
          rows={sessions.map((session) => ({
            session,
            agentName: validAgent.name,
            notifState: notificationContext.notificationState.get(session.id) ?? null,
            isPending: false,
            hasDraft: draftSessionIds.has(session.id),
            isTaskSession: isProjectTaskSession(session),
            onSelect: onOpenSession,
          }))}
          increaseViewportBy={400}
          endReached={() => { if (hasMore && !loadingMore) void loadMore(); }}
          footer={footer()}
        />
      )}
    </main>
  );
}
